import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { ResumeService } from "../../services/resumeService";
import { ProfileService } from "../../services/profileService";
import { ContactService } from "../../services/contactService";
import { resumeData as localResumeData } from "../../data/resumeData";
import "./AIChatModal.scss";

const AIChatModal = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [resumeData, setResumeData] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingResume, setIsLoadingResume] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch profile and resume data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (!profileData) {
        fetchProfileData();
      }
      if (!resumeData) {
        fetchResumeData();
      }
    }
  }, [isOpen, profileData, resumeData]);

  const fetchProfileData = async () => {
    setIsLoadingProfile(true);
    try {
      const data = await ProfileService.getAllProfileData();
      setProfileData(data);
    } catch (error) {
      console.error("Error fetching profile data:", error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const fetchResumeData = async () => {
    setIsLoadingResume(true);
    try {
      // You can set your Google Drive URL here or use an environment variable
      const driveUrl =
        process.env.REACT_APP_RESUME_DRIVE_URL ||
        "https://drive.google.com/file/d/1m5iN5JOahvCqr30DyL-dB9f9jIZ6ei5W/view?usp=sharing";

      const data = await ResumeService.getResumeFromDrive(driveUrl);
      setResumeData(data);
    } catch (error) {
      console.error("Error fetching resume data:", error);
    } finally {
      setIsLoadingResume(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: "user", parts: [{ text: input.trim() }] };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      // Prioritize local resume data over Google Drive data
      let contextData =
        localResumeData || resumeData || ResumeService.getStaticResumeData();

      if (profileData) {
        const formattedProfileData =
          ProfileService.formatProfileDataForAI(profileData);
        contextData += `\n\n**Real-time Profile Data:**${formattedProfileData}`;
      }

      const chatHistory = [
        {
          role: "user",
          parts: [
            {
              text: `You are Dhrumit Patel's AI Portfolio Assistant. You have access to comprehensive information about Dhrumit's professional background, skills, experience, and projects. Use this information to provide accurate, helpful, and professional responses.

IMPORTANT GUIDELINES:
1. **Use ONLY the provided data** to answer questions about Dhrumit
2. **Be professional, friendly, and helpful** in your responses
3. **If you don't have specific information**, politely redirect to contact options
4. **Always provide context** and relevant details when possible
5. **Use proper formatting** for better readability

RESPONSE FORMATTING:
- Use **bold text** for key terms, achievements, and important information
- Use *italics* for emphasis and descriptions
- Use \`code formatting\` for technical terms, languages, and tools
- Use bullet points (•) for lists
- Use clear section headers (###) for organization
- Include relevant metrics and achievements when available

WHEN YOU CAN'T ANSWER A QUESTION:
If someone asks about information not available in the provided data, respond with the following formatted response:

${ContactService.getFallbackResponse()}

CONTACT INFORMATION:
${ContactService.getFormattedContactInfo()}

CURRENT DATA CONTEXT:
${contextData}

Remember: You are representing Dhrumit professionally. Be helpful, accurate, and always provide contact information when you can't answer specific questions.`,
            },
          ],
        },
        ...messages,
        userMessage,
      ];

      const payload = { contents: chatHistory };
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      // Debug: print only a short prefix so the full key is not exposed in logs
      console.log(
        "GEMINI API KEY loaded (prefix):",
        apiKey ? apiKey.slice(0, 10) : "undefined",
      );
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("API Error:", errorData);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            role: "model",
            parts: [
              {
                text: `**Oops!** Something went wrong while connecting to the AI. Please try again.

${ContactService.getFallbackResponse()}`,
              },
            ],
          },
        ]);
      } else {
        const result = await response.json();
        if (
          result.candidates &&
          result.candidates.length > 0 &&
          result.candidates[0].content &&
          result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0
        ) {
          const aiResponseText = result.candidates[0].content.parts[0].text;
          setMessages((prevMessages) => [
            ...prevMessages,
            { role: "model", parts: [{ text: aiResponseText }] },
          ]);
        } else {
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              role: "model",
              parts: [
                {
                  text: `**Error:** I did not receive a valid response from the AI. Please try rephrasing your question.

${ContactService.getFallbackResponse()}`,
                },
              ],
            },
          ]);
        }
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          role: "model",
          parts: [
            {
              text: `**Network Error:** An unexpected error occurred. Please check your network connection and try again.

${ContactService.getFallbackResponse()}`,
            },
          ],
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isLoading) {
      sendMessage();
    }
  };

  const handleClose = () => {
    onClose();
    // Clear messages when closing
    setMessages([]);
    setInput("");
  };

  // Handle link clicks in AI responses
  const handleLinkClick = (url, isExternal = false) => {
    if (isExternal) {
      // Open external links in new tab
      window.open(url, "_blank");
    } else {
      // For internal portfolio links, close modal and navigate
      handleClose();
      // Small delay to ensure modal closes before navigation
      setTimeout(() => {
        if (url.includes("#contact")) {
          // Navigate to contact section
          const contactSection = document.getElementById("contact");
          if (contactSection) {
            contactSection.scrollIntoView({ behavior: "smooth" });
          }
        } else if (url.includes("#")) {
          // Navigate to other sections
          const sectionId = url.split("#")[1];
          const section = document.getElementById(sectionId);
          if (section) {
            section.scrollIntoView({ behavior: "smooth" });
          }
        }
      }, 300);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="aichat-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className="aichat-modal-content"
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aichat-modal-header">
              <h2>AI Portfolio Assistant</h2>
              <button className="aichat-modal-close" onClick={handleClose}>
                ×
              </button>
            </div>

            <div className="aichat-modal-body">
              {(isLoadingProfile || isLoadingResume) && (
                <div className="aichat-loading-profile">
                  <p>Loading your latest data...</p>
                  {isLoadingProfile && <p>Fetching profile information...</p>}
                  {isLoadingResume && <p>Fetching resume data...</p>}
                </div>
              )}

              <div className="aichat-messages">
                {messages.length === 0 && (
                  <div className="aichat-welcome">
                    <h3>👋 Hello! I'm Dhrumit's AI Portfolio Assistant</h3>
                    <p>
                      I have comprehensive information about Dhrumit's
                      professional background, including his all skills,
                      projects, and achievements.
                    </p>
                    <p>I can help you learn about:</p>
                    <ul>
                      <li>
                        💼 <strong>Current work experience</strong> and
                        professional background
                      </li>
                      <li>
                        🛠️ <strong>Technical skills</strong> and programming
                        languages
                      </li>
                      <li>
                        🚀 <strong>Projects</strong> and GitHub repositories
                      </li>
                      <li>
                        🤗 <strong>Hugging Face</strong> models and
                        contributions
                      </li>
                      <li>
                        🎓 <strong>Education</strong> and certifications
                      </li>
                      <li>
                        📊 <strong>Real-time data</strong> from his social
                        profiles
                      </li>
                    </ul>
                    <p className="example-label">
                      <em>💡 Try asking:</em>
                    </p>
                    <ul>
                      <li>"Tell me about his machine learning projects"</li>
                      <li>"What are his key technical skills?"</li>
                      <li>"Show me his latest GitHub projects"</li>
                    </ul>
                    <p className="contact-note">
                      <em>
                        💬 If I don't have specific information, I'll help you
                        get in touch with Dhrumit directly!
                      </em>
                    </p>
                  </div>
                )}
                {messages.map((msg, index) => (
                  <motion.div
                    key={index}
                    className={`aichat-message ${msg.role === "user" ? "user" : "ai"}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="message-content">
                      <ReactMarkdown
                        components={{
                          a: ({ href, children }) => {
                            const isExternal = href.startsWith("http");
                            const isContactForm = href.includes("#contact");

                            return (
                              <a
                                href={href}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleLinkClick(href, isExternal);
                                }}
                                className={`aichat-link ${isContactForm ? "contact-link" : ""}`}
                                target={isExternal ? "_blank" : undefined}
                                rel={
                                  isExternal ? "noopener noreferrer" : undefined
                                }
                              >
                                {children}
                              </a>
                            );
                          },
                        }}
                      >
                        {msg.parts[0].text}
                      </ReactMarkdown>
                    </div>
                  </motion.div>
                ))}
                {isLoading && (
                  <motion.div
                    className="aichat-message ai"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="message-content">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="aichat-input">
                <input
                  type="text"
                  placeholder="Ask me a question..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                />
                <button
                  className="aichat-send-btn"
                  onClick={sendMessage}
                  disabled={isLoading}
                >
                  Send
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AIChatModal;
